import { readAuthErrorCode, readApiErrorCode } from '@liquid-spirit/api/client';
import { jwtDecode } from 'jwt-decode';
import {
  AUTH_BASE_PATH,
  AUTH_PASSKEY_PATHS,
  AUTH_PATHS,
  readGoogleTokenRequest,
  readAppleTokenRequest,
  readAuthVerificationStartRequest,
  readAuthIdentityPathRequest,
  readAuthRecoveryRequest,
  readAuthResetPasswordRequest,
  readAuthChangePasswordRequest,
  readAuthResetTokenRequest,
  readAuthProfileUpdateRequest,
  readMemberRegistrationRequest,
  readMemberVerificationRequest,
  readPasswordLoginRequest,
  readRefreshTokenRequest,
  buildDeleteUserPath,
  buildPasskeyCredentialPath,
  buildValidateResetTokenPath,
  readAuthMeResponse,
  readAuthProfileUpdateResponse,
  readAuthActionResponse,
  readAuthResetTokenResponse,
  readAuthSessionResponse,
  readAuthRegisterResponse,
  readAuthVerificationCompletionResponse,
  readAuthProviderResponse,
} from '@liquid-spirit/api/domains/auth';
import { sanitizeInput } from '../utils/miscHelper';
import { resolveStoredAuthToken } from '../utils/storage';
import { dataFlowLog } from '../utils/dataFlowLogger';
import apiClient from './apiClient';
import { API_BASE_URL } from '../config/apiBase';

const RAW_AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL?.trim?.();
const FORCE_REMOTE_AUTH = String(import.meta.env.VITE_AUTH_USE_REMOTE || '').trim().toLowerCase() === 'true';

const normalizeAuthBase = (baseUrl) => {
  if (typeof baseUrl !== 'string') {
    return null;
  }

  const trimmed = baseUrl.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed, window.location.origin);
    const rootPath = (parsed.pathname || '').replace(/\/$/, '');
    if (rootPath === '' || rootPath === '/') {
      return `${parsed.origin}${AUTH_BASE_PATH}`;
    }

    if (rootPath.endsWith(AUTH_BASE_PATH)) {
      return `${parsed.origin}${rootPath}`;
    }

    if (rootPath.endsWith('/auth')) {
      return `${parsed.origin}${rootPath}`;
    }

    if (rootPath.endsWith('/api')) {
      return `${parsed.origin}${rootPath}/auth`;
    }

    return `${parsed.origin}${rootPath}${AUTH_BASE_PATH}`;
  } catch {
    if (trimmed.startsWith('/')) {
      return `${trimmed.replace(/\/$/, '')}${AUTH_BASE_PATH}`;
    }
  }

  return null;
};

export const resolveAuthApiBase = ({
  apiUrl = API_BASE_URL,
  rawAuthApiUrl = RAW_AUTH_API_URL,
  forceRemoteAuth = FORCE_REMOTE_AUTH,
  isProd = import.meta.env.PROD,
  hostname = typeof window !== 'undefined' ? window.location.hostname : null,
} = {}) => {
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';

  // In production/public builds, always use same-origin auth path to avoid CORS.
  if (isProd && !isLocalHost) {
    return AUTH_BASE_PATH;
  }

  if (!hostname) {
    return AUTH_BASE_PATH;
  }

  if (!isLocalHost) {
    return AUTH_BASE_PATH;
  }

  const remoteAuthBase = normalizeAuthBase(rawAuthApiUrl);
  if (remoteAuthBase) {
    return remoteAuthBase;
  }

  const localAuthBase = normalizeAuthBase(apiUrl);
  if (localAuthBase) {
    return localAuthBase;
  }

  if (!forceRemoteAuth) {
    return AUTH_BASE_PATH;
  }

  return remoteAuthBase || AUTH_BASE_PATH;
};

const AUTH_API_URL = resolveAuthApiBase();

dataFlowLog('auth-service.endpoint', { authApiUrl: AUTH_API_URL });

const parseMaybeObject = (value, fallback = null) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  return fallback;
};

const bufferToBase64Url = (value) => {
  if (!value) {
    return '';
  }

  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    const chunk = bytes.subarray(i, i + 0x8000);
    binary += String.fromCharCode(...chunk);
  }

  let base64 = '';

  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    base64 = window.btoa(binary);
  } else if (globalThis.Buffer && typeof globalThis.Buffer.from === 'function') {
    base64 = globalThis.Buffer.from(bytes).toString('base64');
  }
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const base64UrlToBuffer = (base64url) => {
  if (!base64url || typeof base64url !== 'string') {
    return new Uint8Array();
  }

  const base64 = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(base64url.length / 4) * 4, '=');

  if (typeof window === 'undefined' || typeof window.atob !== 'function') {
    if (!globalThis.Buffer || typeof globalThis.Buffer.from !== 'function') return new Uint8Array();
    const binary = globalThis.Buffer.from(base64, 'base64');
    return new Uint8Array(binary);
  }

  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

const credentialRegistrationOptionsFromServer = (options) => {
  const clone = { ...options };
  clone.challenge = base64UrlToBuffer(options.challenge);

  if (clone.user?.id) {
    clone.user = {
      ...clone.user,
      id: base64UrlToBuffer(clone.user.id),
    };
  }

  if (Array.isArray(clone.excludeCredentials)) {
    clone.excludeCredentials = clone.excludeCredentials.map((item) => ({
      ...item,
      id: base64UrlToBuffer(item.id),
    }));
  }

  return clone;
};

const credentialRequestOptionsFromServer = (options) => {
  const clone = { ...options };
  clone.challenge = base64UrlToBuffer(options.challenge);

  if (Array.isArray(clone.allowCredentials)) {
    clone.allowCredentials = clone.allowCredentials.map((item) => ({
      ...item,
      id: base64UrlToBuffer(item.id),
    }));
  }

  return clone;
};

const formatWebAuthnCredentialForServer = (credential) => ({
  id: credential.id,
  rawId: bufferToBase64Url(new Uint8Array(credential.rawId)),
  type: credential.type,
  response: {
    attestationObject: credential.response.attestationObject
      ? bufferToBase64Url(new Uint8Array(credential.response.attestationObject))
      : '',
    clientDataJSON: bufferToBase64Url(new Uint8Array(credential.response.clientDataJSON)),
    transports: typeof credential.response.getTransports === 'function'
      ? credential.response.getTransports()
      : undefined,
  },
  clientExtensionResults: credential.getClientExtensionResults
    ? credential.getClientExtensionResults()
    : {},
});

const formatWebAuthnAssertionForServer = (assertion) => ({
  id: assertion.id,
  rawId: bufferToBase64Url(new Uint8Array(assertion.rawId)),
  type: assertion.type,
  response: {
    authenticatorData: bufferToBase64Url(new Uint8Array(assertion.response.authenticatorData)),
    clientDataJSON: bufferToBase64Url(new Uint8Array(assertion.response.clientDataJSON)),
    signature: bufferToBase64Url(new Uint8Array(assertion.response.signature)),
    userHandle: assertion.response.userHandle
      ? bufferToBase64Url(new Uint8Array(assertion.response.userHandle))
      : undefined,
  },
  clientExtensionResults: assertion.getClientExtensionResults
    ? assertion.getClientExtensionResults()
    : {},
});

const requestAuthService = async ({
  path,
  method = 'GET',
  body,
  token,
  auth = true,
  errorLabel
}) => {
  try {
    const useToken = auth ? resolveStoredAuthToken(token) : token;
    const shouldDebugAuthUrl =
      typeof window !== 'undefined' &&
      window.localStorage?.getItem('ls:auth-debug-url') === '1';

    const rawRequestPath = shouldDebugAuthUrl
    ? `${path}${path.includes('?') ? '&' : '?'}_authBase=${encodeURIComponent(AUTH_API_URL)}`
    : path;

  const isAuthRoutePath = typeof rawRequestPath === 'string' && rawRequestPath.includes('/api/auth/');
  const isAbsoluteUrl =
    typeof rawRequestPath === 'string' && /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(rawRequestPath);

  const requestPath =
    isAuthRoutePath && typeof window !== 'undefined' && !isAbsoluteUrl
      ? `${window.location.origin}${rawRequestPath}`
      : rawRequestPath;

    const result = await apiClient.request(requestPath, {
      method,
      body,
      token: useToken,
      auth: Boolean(auth),
      contract: 'raw',
      throwOnError: false,
    });

    return {
      ok: result.response.ok,
      data: result.payload,
      status: result.response.status,
      ...(readAuthErrorCode(result.response, result.payload) ? {authCode: readAuthErrorCode(result.response, result.payload)} : {}),
      ...(readApiErrorCode(result.response, result.payload) ? {apiCode: readApiErrorCode(result.response, result.payload)} : {}),
    };
  } catch (error) {
    throw new Error(`${errorLabel}: ${error.message}`);
  }
};

export const signIn = async (email, password) => {
  readPasswordLoginRequest({email, password});
  const payload = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.login}`,
    method: 'POST',
    body: JSON.stringify({
      email: sanitizeInput(email),
      password: sanitizeInput(password),
    }),
    auth: false,
    errorLabel: 'Sign-in error',
  });

  if (payload.ok) readAuthSessionResponse(payload.data);
  return payload;
};

export const refreshAuthToken = async (refreshToken) => {
  readRefreshTokenRequest({refreshToken});
  const result = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.refresh}`,
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
    auth: false,
    errorLabel: 'Refresh token error',
  });
  if (result.ok) readAuthSessionResponse(result.data);
  return result;
};

export const signUp = async (email, bahaiId, password, eulaAcceptedAt) => {
  readMemberRegistrationRequest({email, bahaiId, eulaAcceptedAt});
  const result = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.register}`,
    method: 'POST',
    body: JSON.stringify({
      email: sanitizeInput(email),
      bahaiId: sanitizeInput(bahaiId),
      password: sanitizeInput(password),
      eulaAcceptedAt,
    }),
    auth: false,
    errorLabel: 'Sign-up error',
  });
  if (result.ok) readAuthRegisterResponse(result.data);
  return result;
};

export const verify = async (bahaiId, verificationCode, password) => {
  readMemberVerificationRequest({bahaiId, verificationCode, password});
  const payload = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.verify}`,
    method: 'POST',
    body: JSON.stringify({
      bahaiId: sanitizeInput(bahaiId),
      verificationCode: sanitizeInput(verificationCode),
      password: sanitizeInput(password),
    }),
    auth: false,
    errorLabel: 'Sign-up error',
  });

  if (payload.ok) readAuthVerificationCompletionResponse(payload.data);

  dataFlowLog('auth-service.verify.response', {
    ok: payload?.ok,
    status: payload?.status,
    hasToken: Boolean(payload?.data?.token),
  });
  return payload;
};

export const fetchMe = async (token = null) => {
  const result = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.me}`,
    method: 'GET',
    auth: true,
    token,
    errorLabel: 'Sign-in error',
  });
  if (result.ok) readAuthMeResponse(result.data);
  return result;
};

export const updateMe = async (updatedUser, token = null) => {
  readAuthProfileUpdateRequest(updatedUser);
  const result = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.me}`,
    method: 'PUT',
    token: token || resolveStoredAuthToken(),
    body: JSON.stringify(updatedUser),
    errorLabel: 'Update error',
  });
  if (result.ok) readAuthProfileUpdateResponse(result.data);
  return result;
};

export const getCurrentUserId = () => {
  const token = resolveStoredAuthToken();
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return decoded.userId || decoded.id;
  } catch (error) {
    dataFlowLog('auth-service.token.decode.error', {
      source: 'getCurrentUserId',
      message: error?.message || 'Unknown error',
    });
    return null;
  }
};

export const checkTokenExpiration = () => {
  const token = resolveStoredAuthToken();

  if (!token) {
    dataFlowLog('auth-service.token.expiry.invalid', {
      reason: 'No token found',
    });
    return { isValid: false, reason: 'No token found' }; // No token found
  }

  // Check if token has the correct format with 3 parts
  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    dataFlowLog('auth-service.token.expiry.invalid', {
      reason: 'Invalid token format',
    });
    return { isValid: false, reason: 'Invalid token format' }; // Invalid token format
  }

  try {
    const decodedToken = jwtDecode(token);

    // Check if the token has expired
    if (decodedToken.exp * 1000 < Date.now()) {
      dataFlowLog('auth-service.token.expiry.invalid', {
        reason: 'Token expired',
      });
      return { isValid: false, reason: 'Token expired' }; // Token expired
    }

    return { isValid: true }; // Token is valid
  } catch (error) {
    dataFlowLog('auth-service.token.expiry.invalid', {
      reason: 'Error decoding token',
      message: error?.message || 'Unknown error',
    });
    return { isValid: false, reason: 'Error decoding token' }; // Decoding error
  }
};

export const googleSignIn = async (idToken) => {
  readGoogleTokenRequest({ token: idToken });
  const payload = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.google}`,
    method: 'POST',
    body: JSON.stringify({ token: idToken }),
    auth: false,
    errorLabel: 'Google sign-in error',
  });

  if (payload.ok) readAuthProviderResponse(payload.data);

  if (payload.ok && payload.data?.token) return { ok: true, data: payload.data };

  dataFlowLog('auth-service.google-sign-in.error', {
    ok: payload.ok,
    status: payload.status,
    message: payload.data?.message,
    errors: payload.data?.errors,
  });
  return { ok: false, data: payload.data, status: payload.status, apiCode: payload.apiCode, authCode: payload.authCode };
};

export const appleSignIn = async (body = {}) => {
  readAppleTokenRequest(body);
  const {
    identityToken,
    authorizationCode,
    nonce,
    email,
    fullName,
    user,
    redirectURI,
  } = body;
  dataFlowLog('auth-service.apple-sign-in.request', {
    hasIdentityToken: Boolean(identityToken),
    hasAuthorizationCode: Boolean(authorizationCode),
    noncePresent: Boolean(nonce),
    emailProvided: Boolean(email),
    hasFullName: Boolean(fullName),
    hasUserObject: Boolean(user),
    hasRedirectUri: Boolean(redirectURI),
  });

  const payload = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.apple}`,
    method: 'POST',
    body: JSON.stringify({
      identityToken,
      authorizationCode,
      nonce,
      email,
      fullName,
      user,
      redirectURI,
    }),
    auth: false,
    errorLabel: 'Apple sign-in error',
  });

  if (payload.ok) readAuthProviderResponse(payload.data);

  dataFlowLog('auth-service.apple-sign-in.response', {
    ok: payload.ok,
    status: payload.status,
    hasToken: Boolean(payload.data?.token),
    message: payload.data?.message,
  });

  if (payload.ok && payload.data?.token) {
    return { ok: true, data: payload.data };
  }
  return { ok: false, data: payload.data, status: payload.status, apiCode: payload.apiCode, authCode: payload.authCode };
};

// Retrieve and parse user information from token after Google sign-in
export const fetchGoogleUserInfo = () => {
  const token = resolveStoredAuthToken();
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return decoded;
  } catch (error) {
    dataFlowLog('auth-service.token.decode.error', {
      source: 'fetchGoogleUserInfo',
      message: error?.message || 'Unknown error',
    });
    return null;
  }
};

export const requestPasswordResetEmail = async (email) => {
  readAuthRecoveryRequest({email});
  const result = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.forgotPassword}`,
    method: 'POST',
    body: JSON.stringify({ email }),
    auth: false,
    errorLabel: 'Forgot password error',
  });
  if (result.ok) readAuthActionResponse(result.data);
  return result;
};

export const validateResetPasswordToken = async (token) => {
  readAuthResetTokenRequest(token);
  const payload = await requestAuthService({
    path: `${AUTH_API_URL}${buildValidateResetTokenPath(token)}`,
    method: 'GET',
    auth: false,
    errorLabel: 'Validate reset token error',
  });
  if (payload.ok) readAuthResetTokenResponse(payload.data);

  return {
    ok: payload.ok,
    data: parseMaybeObject(payload.data, {}),
    ...(payload.authCode ? {authCode: payload.authCode} : {}),
    ...(payload.apiCode ? {apiCode: payload.apiCode} : {}),
  };
};

export const resetPasswordWithToken = async (token, newPassword) => {
  readAuthResetPasswordRequest({token, newPassword});
  const payload = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.resetPassword}`,
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
    auth: false,
    errorLabel: 'Reset password error',
  });
  if (payload.ok) readAuthActionResponse(payload.data);

  return {
    ok: payload.ok,
    data: parseMaybeObject(payload.data, {}),
    ...(payload.authCode ? {authCode: payload.authCode} : {}),
    ...(payload.apiCode ? {apiCode: payload.apiCode} : {}),
  };
};

export const changePassword = async ({ currentPassword, newPassword, token = null }) => {
  readAuthChangePasswordRequest({currentPassword, newPassword});
  const sanitizedCurrentPassword = sanitizeInput(currentPassword);
  const sanitizedNewPassword = sanitizeInput(newPassword);
  const requestBody = JSON.stringify({
    currentPassword: sanitizedCurrentPassword,
    newPassword: sanitizedNewPassword,
  });
  const payload = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.changePassword}`,
    method: 'POST',
    body: requestBody,
    token,
    auth: true,
    errorLabel: 'Change password error',
  });
  if (payload.ok) readAuthActionResponse(payload.data);
  return {
    ok: payload.ok,
    data: parseMaybeObject(payload.data, {}),
    ...(payload.authCode ? {authCode: payload.authCode} : {}),
    ...(payload.apiCode ? {apiCode: payload.apiCode} : {}),
    status: payload.status,
  };
};

// Trigger backend to email the user their Baha’i ID given an email
export const forgotBahaiId = async (email) => {
  readAuthRecoveryRequest({email});
  const result = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.forgotBahaiId}`,
    method: 'POST',
    body: JSON.stringify({ email }),
    auth: false,
    errorLabel: 'Forgot Baha\'i ID error',
  });
  if (result.ok) readAuthActionResponse(result.data);
  return result;
};

export const requestBahaiIdVerification = async (bahaiId, email) => {
  readAuthVerificationStartRequest({bahaiId, email});
  const result = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PATHS.requestBahaiIdVerification}`,
    method: 'POST',
    body: JSON.stringify({bahaiId: sanitizeInput(bahaiId), email: sanitizeInput(email)}),
    auth: false,
    errorLabel: 'Request Baha\'i ID verification error',
  });
  if (result.ok) readAuthActionResponse(result.data);
  return result;
};

export const deleteAccount = async (userId) => {
  readAuthIdentityPathRequest(userId);
  try {
    const payload = await requestAuthService({
      path: `${AUTH_API_URL}${buildDeleteUserPath(userId)}`,
      method: 'DELETE',
      token: resolveStoredAuthToken(),
      errorLabel: 'Delete account error',
    });

    if (!payload.ok) {
      const errorData = parseMaybeObject(payload.data, null);
      throw Object.assign(new Error(errorData?.message || 'Failed to delete account'), {authCode: payload.authCode, apiCode: payload.apiCode});
    }

    readAuthActionResponse(payload.data);
    return { ok: true };
  } catch (error) {
    dataFlowLog('auth-service.delete-account.error', {
      userId: userId || null,
      message: error?.message || 'Unknown error',
    });
    return { ok: false, message: error.message, ...(error.authCode ? {authCode: error.authCode} : {}), ...(error.apiCode ? {apiCode: error.apiCode} : {}) };
  }
};


export const getPasskeyCredentials = async (token = null) => requestAuthService({
  path: `${AUTH_API_URL}${AUTH_PASSKEY_PATHS.credentials}`,
  method: 'GET',
  auth: true,
  token,
  errorLabel: 'Passkey list error',
});

export const deletePasskeyCredential = async (credentialId, token = null) => requestAuthService({
  path: `${AUTH_API_URL}${buildPasskeyCredentialPath(credentialId || '')}`,
  method: 'DELETE',
  auth: true,
  token,
  errorLabel: 'Passkey revoke error',
});

export const getPasskeyAuthenticateOptions = async () => requestAuthService({
  path: `${AUTH_API_URL}${AUTH_PASSKEY_PATHS.authenticateOptions}`,
  method: 'POST',
  auth: false,
  errorLabel: 'Passkey authenticate options error',
});

export const verifyPasskeyAuthentication = async (payload) => {
  const response = await requestAuthService({
    path: `${AUTH_API_URL}${AUTH_PASSKEY_PATHS.authenticateVerify}`,
    method: 'POST',
    body: JSON.stringify(payload),
    auth: false,
    errorLabel: 'Passkey authentication error',
  });

  return response;
};

export const getPasskeyRegisterOptions = async (token = null) => requestAuthService({
  path: `${AUTH_API_URL}${AUTH_PASSKEY_PATHS.registerOptions}`,
  method: 'POST',
  auth: true,
  token,
  errorLabel: 'Passkey register options error',
});

export const verifyPasskeyRegistration = async (payload, token = null) => requestAuthService({
  path: `${AUTH_API_URL}${AUTH_PASSKEY_PATHS.registerVerify}`,
  method: 'POST',
  body: JSON.stringify(payload),
  auth: true,
  token,
  errorLabel: 'Passkey registration verification error',
});

export const isWebAuthnSupported = () => Boolean(
  typeof window !== 'undefined'
  && window.PublicKeyCredential
  && typeof window.PublicKeyCredential !== 'undefined'
);

export const convertPasskeyRegistrationOptions = credentialRegistrationOptionsFromServer;
export const convertPasskeyAuthenticationOptions = credentialRequestOptionsFromServer;
export const formatPasskeyCredentialForServer = formatWebAuthnCredentialForServer;
export const formatPasskeyAssertionForServer = formatWebAuthnAssertionForServer;
